/**
 * AccountContext
 * Single poller for /api/account, shared by Layout (header ticker), Dashboard
 * (KPI cards), and Trades so all three read the same value instead of
 * independent polls that briefly disagree. Also derives the live capital
 * deployment tier (GREEN/YELLOW/ORANGE/RED) — mirrors the boundaries in
 * sf_signal_scanner.py (~line 3501-3515): RED blocks all new BUYs.
 *
 * Account switcher (2026-09-19): Saferfunds runs two real, simultaneously-live
 * paper books on separate Alpaca accounts (2026-11-03: a third, JoshSkills, lives outside the engine and is not switchable here) — MAIN (the original account, tied
 * to docs/GOAL_RECOVER_TO_100K.md) and Tier 2 (a separate account, started at
 * $50K). This context now holds `selectedAccount` ('main' | 'tier2'),
 * switchable from the header, persisted to localStorage so it's sticky across
 * reloads. Everything reading `account` from this context (header ticker,
 * Dashboard's Portfolio Value/Buying Power/Day-Trade tiles, Trades) follows
 * the switch. The Path-to-$100K card is the one deliberate exception — it
 * fetches MAIN directly, independent of this switcher, since a $100K target
 * only means anything against the account it was actually set for (this is
 * exactly the bug being fixed: the card silently showed Tier 2's numbers
 * against a $100K target for over a week because the old default was
 * hardcoded and nothing here was switchable at all).
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { api, systemConfig } from '../lib/api'

const AccountContext = createContext(null)
const STORAGE_KEY = 'sf_selected_account'

function loadStoredAccount() {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY)
    return (v === 'main' || v === 'tier2') ? v : 'main'
  } catch {
    return 'main'
  }
}

function computeTier(account, minBuyingPowerPct) {
  if (!account || account.portfolio_value == null || !account.portfolio_value) return null
  const bpRatioPct = (account.buying_power / account.portfolio_value) * 100
  const floor = minBuyingPowerPct ?? 10.0
  let tier
  if (bpRatioPct >= 50) tier = 'GREEN'
  else if (bpRatioPct >= 25) tier = 'YELLOW'
  else if (bpRatioPct >= floor) tier = 'ORANGE'
  else tier = 'RED'
  return { bpRatioPct, minBuyingPowerPct: floor, tier }
}

export function AccountProvider({ children }) {
  const [account, setAccount] = useState(null)
  const [minBuyingPowerPct, setMinBuyingPowerPct] = useState(null)
  const [selectedAccount, setSelectedAccountState] = useState(loadStoredAccount)

  const setSelectedAccount = useCallback((acct) => {
    if (acct !== 'main' && acct !== 'tier2') return
    setSelectedAccountState(acct)
    try { window.localStorage.setItem(STORAGE_KEY, acct) } catch { /* per-viewer convenience only */ }
  }, [])

  const refresh = useCallback(async () => {
    try {
      const acct = await api.account(selectedAccount)
      if (acct.success) setAccount(acct)
    } catch { /* account data is optional — don't fail silently */ }
  }, [selectedAccount])

  const refreshConfig = useCallback(async () => {
    try {
      const cfg = await systemConfig.get()
      if (cfg.success) setMinBuyingPowerPct(cfg.execution?.min_buying_power_pct)
    } catch { /* floor rarely changes — fall back to the 10% default */ }
  }, [])

  useEffect(() => {
    refresh()
    const acctId = setInterval(refresh, 30_000)
    return () => clearInterval(acctId)
  }, [refresh])

  useEffect(() => {
    refreshConfig()
    const cfgId = setInterval(refreshConfig, 300_000)
    return () => clearInterval(cfgId)
  }, [refreshConfig])

  const tierInfo = computeTier(account, minBuyingPowerPct)

  return (
    <AccountContext.Provider value={{ account, ...tierInfo, refresh, selectedAccount, setSelectedAccount }}>
      {children}
    </AccountContext.Provider>
  )
}

export function useAccount() {
  const ctx = useContext(AccountContext)
  if (!ctx) throw new Error('useAccount must be used inside AccountProvider')
  return ctx
}
